import {
  Alert,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import {
  useStartAgent,
  useAgentMessage,
  useGetAgentsThread,
  useInterruptAgentThread,
} from "services/agents";
import {
  useTaskAgentState,
  processEventsWithAddedData,
} from "components/hooks/useTaskAgentState";
import { useListTasksForFamily } from "services/tasks";
import { Task } from "protogen/tasks_pb";
import { GetAgentsThreadRequest } from "protogen/agents_service_pb";
import { RichContent } from "components/editor/utils";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import { ArrowUp, RotateCcw } from "lucide-react";
import TaskAgentDisplay from "./TaskAgentDisplay";
import Loading from "components/common/Loading";

// Demo component - the original TaskAgent functionality
export default () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [description, setDescription] = useState<string | null>(
    searchParams.get("description") || null,
  );
  const [selectedFamily, setSelectedFamily] = useState<string | null>(
    searchParams.get("family") || null,
  );
  const [selectedModel, setSelectedModel] = useState<string>(
    searchParams.get("model") || "gpt-5-mini",
  );
  const [tasks, setTasks] = useState<Task[]>([]);
  const [threadRef, setThreadRef] = useState<string | null>(
    searchParams.get("threadRef") || null,
  );
  const [isInterrupting, setIsInterrupting] = useState(false);

  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { request, loading, error } = useStartAgent();
  const [hasStarted, setHasStarted] = useState(false);
  const [hasActivated, setHasActivated] = useState(false);
  const { request: agentMessage } = useAgentMessage();
  const [currentThreadRef, setCurrentThreadRef] = useState<string | null>(null);
  const stateManager = useTaskAgentState();
  const { request: listTasksForFamily, loading: loadingTasks } =
    useListTasksForFamily();
  const { request: getAgentsThread } = useGetAgentsThread();
  const { request: interruptAgentThread } = useInterruptAgentThread();

  useEffect(() => {
    if (threadRef) {
      const channel = `task-agent/${encodeURIComponent(threadRef)}`;
      registerHandler(channel, stateManager.actions.handleWebSocketMessage);

      return () => {
        unregisterHandler(channel, stateManager.actions.handleWebSocketMessage);
      };
    }
  }, [
    registerHandler,
    unregisterHandler,
    stateManager.actions.handleWebSocketMessage,
    threadRef,
  ]);

  useEffect(() => {
    if (stateManager.state.isActive) {
      setHasActivated(true);
    }
  }, [stateManager.state.isActive]);

  // Load thread state if threadRef is present in URL
  useEffect(() => {
    const loadThreadState = async (threadRef: string) => {
      const response = await getAgentsThread(
        new GetAgentsThreadRequest({ threadRef }),
      );

      const addedEventsMap = new Map<
        number,
        {
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
          sourceEntityType: string;
          sourceEntityId: string;
          sourceEntityIndex: number;
        }
      >();
      if (response?.addedEvents && response.addedEvents.length > 0) {
        response.addedEvents.forEach((addedEvent) => {
          addedEventsMap.set(addedEvent.sourceEntityIndex, {
            eventRef: addedEvent.eventRef,
            eventStatus: addedEvent.eventStatus,
            eventModified: addedEvent.eventModified,
            sourceEntityType: addedEvent.sourceEntityType,
            sourceEntityId: addedEvent.sourceEntityId,
            sourceEntityIndex: addedEvent.sourceEntityIndex,
          });
        });
      }

      // Process events with added data
      const processedEvents = processEventsWithAddedData(
        response?.events || [],
        addedEventsMap,
      );
      processedEvents.forEach((eventString: string) => {
        const event = JSON.parse(eventString);
        stateManager.actions.handleWebSocketMessage("", event);
      });

      setHasStarted(true);
      setHasActivated(true);
      setCurrentThreadRef(threadRef);
    };
    if (searchParams.get("threadRef")) {
      loadThreadState(searchParams.get("threadRef") || "");
    }
  }, []);

  // Fetch tasks when family is selected
  useEffect(() => {
    const getTasks = async () => {
      if (selectedFamily) {
        const response = await listTasksForFamily({
          familyRef: selectedFamily,
          taskStates: [],
          taskStages: [],
        });
        setTasks(response?.tasks || []);
        if (searchParams.get("task")) {
          setSelectedTask(
            response?.tasks.find((t) => t.ref === searchParams.get("task")) ||
              null,
          );
        }
      }
    };
    getTasks();
  }, [selectedFamily]);

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (description) {
      params.set("description", description);
    }

    if (selectedTask) {
      params.set("task", selectedTask.ref);
    }

    if (selectedFamily) {
      params.set("family", selectedFamily);
    }

    if (selectedModel !== "gpt-5-mini") {
      params.set("model", selectedModel);
    }

    if (threadRef) {
      params.set("threadRef", threadRef);
    }

    setSearchParams(params, { replace: true });
  }, [
    description,
    selectedTask,
    selectedFamily,
    selectedModel,
    threadRef,
    setSearchParams,
  ]);

  const onSubmit = async () => {
    if (!selectedFamily || !(selectedTask || description)) {
      return;
    }
    setHasStarted(true);

    const message = `Help me solve this task: ${
      selectedTask?.title || description
    }`;

    const response = await request({
      agentType: selectedTask?.ref ? "task" : "adhoc",
      arguments: selectedTask?.ref
        ? [
            {
              name: "taskRef",
              value: selectedTask?.ref || "",
            },
          ]
        : [
            {
              name: "familyRef",
              value: selectedFamily || "",
            },
            {
              name: "message",
              value: message,
            },
          ],
      model: selectedModel,
    });
    if (response?.threadRef) {
      setCurrentThreadRef(response.threadRef);
      setThreadRef(response.threadRef);
    }
  };

  const handleUpdatePlanDetails = async (content: RichContent) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }

    // Interrupt the agent first if it's running
    if (stateManager.state.isActive) {
      await handleInterrupt();
      // Wait for interrupt to complete and pending function calls to be cancelled
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    // Mark agent as active so it processes the new information
    stateManager.actions.updateState({
      isActive: true,
      lastEventTime: Date.now(),
    });

    // Send plan details to backend WITHOUT showing in chat
    await agentMessage({
      threadRef: currentThreadRef,
      body: {
        contentType: "html",
        payload: content.html,
        textContent: content.text,
        attachments: content.attachments,
      },
      model: selectedModel,
      hideFromChat: true,
    });
  };

  const handleFollowUpQuestion = async (content: RichContent) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }
    stateManager.actions.updateState({
      chatMessages: [
        ...stateManager.state.chatMessages,
        { content: content.text, source: "user", contentHtml: content.html },
      ],
      isActive: true,
      lastEventTime: Date.now(),
    });

    // Send the follow-up question as a new message in the same thread
    await agentMessage({
      threadRef: currentThreadRef,
      body: {
        contentType: "html",
        payload: content.html, // Send HTML for rich formatting and attachments
        textContent: content.text,
        attachments: content.attachments,
      },
      model: selectedModel,
      hideFromChat: false,
    });
  };

  const handleInterrupt = async () => {
    if (!currentThreadRef || isInterrupting) {
      return;
    }

    setIsInterrupting(true);

    stateManager.actions.updateState({
      isActive: false,
      hasCompleted: true,
    });

    // Minimum delay to prevent jarring UI flash
    await new Promise((resolve) => setTimeout(resolve, 800));

    await interruptAgentThread({
      threadRef: currentThreadRef,
    });

    const response = await getAgentsThread(
      new GetAgentsThreadRequest({ threadRef: currentThreadRef }),
    );

    if (response?.events) {
      const addedEventsMap = new Map<
        number,
        {
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
          sourceEntityType: string;
          sourceEntityId: string;
          sourceEntityIndex: number;
        }
      >();
      if (response.addedEvents && response.addedEvents.length > 0) {
        response.addedEvents.forEach((addedEvent) => {
          addedEventsMap.set(addedEvent.sourceEntityIndex, {
            eventRef: addedEvent.eventRef,
            eventStatus: addedEvent.eventStatus,
            eventModified: addedEvent.eventModified,
            sourceEntityType: addedEvent.sourceEntityType,
            sourceEntityId: addedEvent.sourceEntityId,
            sourceEntityIndex: addedEvent.sourceEntityIndex,
          });
        });
      }

      stateManager.actions.resetState();

      const processedEvents = processEventsWithAddedData(
        response.events || [],
        addedEventsMap,
      );
      processedEvents.forEach((eventString: string) => {
        const event = JSON.parse(eventString);
        stateManager.actions.handleWebSocketMessage("", event);
      });

      stateManager.actions.updateState({
        isActive: false,
        hasCompleted: true,
      });
    }

    setIsInterrupting(false);
  };

  const handleReset = () => {
    // Clear thread and agent state
    setCurrentThreadRef(null);
    setThreadRef(null);
    setHasStarted(false);
    setHasActivated(false);
    stateManager.actions.resetState();

    // Update URL to remove threadRef
    const params = new URLSearchParams();
    if (description) params.set("description", description);
    if (selectedTask) params.set("task", selectedTask.ref);
    if (selectedFamily) params.set("family", selectedFamily);
    if (selectedModel !== "gpt-5-mini") params.set("model", selectedModel);
    setSearchParams(params, { replace: true });
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          marginTop: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
                maxWidth: "200px",
              }}
            >
              <AdminFamilySelect
                selectedFamily={selectedFamily}
                onChange={(f) => {
                  setSelectedFamily(f);
                  setSelectedTask(null); // Reset task selection when family changes
                }}
                title="Select Family"
                disabled={loading || hasStarted}
              />
              <FormControl sx={{ minWidth: "120px" }} size="small">
                <InputLabel>Model</InputLabel>
                <Select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  label="Model"
                  disabled={loading || hasStarted}
                >
                  <MenuItem value="gpt-5-nano">GPT-5 Nano</MenuItem>
                  <MenuItem value="gpt-5-mini">GPT-5 Mini</MenuItem>
                  <MenuItem value="gpt-5">GPT-5</MenuItem>
                  <MenuItem value="gemini-2.5-flash">Gemini 2.5 Flash</MenuItem>
                  <MenuItem value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <FormControl fullWidth size="small">
                <InputLabel>
                  Select Task (or enter description below)
                </InputLabel>
                {loadingTasks || !tasks.length ? (
                  <Select value={""} disabled={true}>
                    <MenuItem value=""></MenuItem>
                  </Select>
                ) : (
                  <Select
                    value={selectedTask?.ref || ""}
                    onChange={(e) => {
                      const taskRef = e.target.value;
                      const task = tasks.find((t) => t.ref === taskRef);
                      setSelectedTask(task || null);
                    }}
                    disabled={
                      loading ||
                      hasStarted ||
                      !selectedFamily ||
                      loadingTasks ||
                      !!description
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    {tasks.map((task) => (
                      <MenuItem
                        sx={{ maxWidth: "350px" }}
                        key={task.ref}
                        value={task.ref}
                      >
                        {task.title}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
              <TextField
                defaultValue={description}
                onChange={(e) => setDescription(e.target.value)}
                label={selectedTask ? selectedTask.title : "Describe a task"}
                disabled={
                  loading ||
                  hasStarted ||
                  !selectedFamily ||
                  loadingTasks ||
                  !!selectedTask
                }
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-end" }}>
              {hasStarted && (
                <IconButton
                  onClick={handleReset}
                  sx={{
                    marginBottom: "8px",
                    borderRadius: "30px",
                    background: "gray",
                    "&:hover": {
                      background: "gray",
                      opacity: 0.8,
                    },
                    padding: "8px",
                  }}
                >
                  <RotateCcw size={26} color="white" />
                </IconButton>
              )}
              <IconButton
                onClick={onSubmit}
                disabled={
                  loading ||
                  !(selectedTask || description) ||
                  !selectedFamily ||
                  hasStarted
                }
                sx={{
                  marginBottom: "8px",
                  borderRadius: "30px",
                  background: "black",
                  "&:hover": {
                    background: "black",
                    opacity: 0.8,
                  },
                  "&:disabled": {
                    background: "black",
                    opacity: 0.5,
                  },
                  padding: "8px",
                }}
              >
                <ArrowUp size={26} color="white" />
              </IconButton>
            </Box>
          </Box>
          {error && (
            <Alert severity="error" sx={{ padding: "12px 24px" }}>
              Error starting agent.
            </Alert>
          )}
        </Box>
        {hasStarted && !hasActivated && <Loading />}
        {currentThreadRef && (
          <Box sx={{ marginTop: "24px" }}>
            <TaskAgentDisplay
              familyRef={selectedFamily || undefined}
              agentThreadRef={currentThreadRef}
              stateManager={stateManager}
              onUpdatePlanDetails={handleUpdatePlanDetails}
              onFollowUpQuestion={handleFollowUpQuestion}
              onInterrupt={handleInterrupt}
              isInterrupting={isInterrupting}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
